/**
 * Full-screen narrated SOP player — an illustrated slide per stage with the
 * voice reading it aloud, the checklist ticking itself off in time with the
 * audio, and a journey map across the top. A stage with a filmed clip plays
 * the clip instead.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  X, Play, Pause, SkipBack, SkipForward, Loader2, Volume2, CheckCircle2, Languages, Check,
  RotateCcw, RotateCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API, API_BASE } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';
import { STAGE_ANIMATIONS } from './StageAnimations';

const abs = (u) => (u && u.startsWith('/') ? `${API_BASE}${u}` : u);
const RING = 2 * Math.PI * 22;
const STEP = 10;   // seconds per rewind / forward tap

const clock = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const m = Math.floor(secs / 60);
  return `${m}:${String(Math.floor(secs % 60)).padStart(2, '0')}`;
};

export const SopPlayer = ({ flow, onClose, onFinished }) => {
  const { authHeader } = useAuth();
  const stages = flow.stages || [];
  const [i, setI] = useState(0);
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [done, setDone] = useState(false);
  const [lang, setLang] = useState('en');
  const [langs, setLangs] = useState([{ code: 'en', label: 'English', native: 'English' }]);
  const [slide, setSlide] = useState(null);
  const [source, setSource] = useState('');
  const [progress, setProgress] = useState(0);   // 0..1 through the stage audio
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const stage = stages[i] || {};
  const isVideo = !!stage.video_url;
  const stageId = stage.id || '';
  const flowId = flow.id;
  // Slide content in the chosen language, falling back to the English source.
  const view = slide && lang !== 'en' ? { ...stage, ...slide } : stage;
  const checklist = view.checklist || [];
  // An animated scene beats a still picture, but a real workshop photo or a
  // filmed clip beats both.
  const ownPhoto = (stage.illustration_url || '').includes('res.cloudinary.com');
  const Animation = ownPhoto ? null : STAGE_ANIMATIONS[`${flow.key}:${stage.order}`];
  // The checklist ticks itself off across the stage audio — evenly spread, so
  // it feels like progress rather than a static list.
  const ticked = Math.floor(progress * (checklist.length + 0.35));

  useEffect(() => {
    axios.get(`${API}/sop/languages`, { headers: authHeader() })
      .then(({ data }) => setLangs(data.languages || []))
      .catch(() => {});
  }, [authHeader]);

  // One fetch per (stage, language). Depending on the stage id rather than the
  // stages array keeps a parent re-render from restarting playback mid-stage.
  useEffect(() => {
    if (!stageId) return undefined;
    let cancelled = false;
    setProgress(0); setElapsed(0); setDuration(0);
    if (isVideo) { setSrc(stage.video_url); setLoading(false); return undefined; }
    setLoading(true); setSrc(''); setSlide(null);
    axios.post(`${API}/sop/flows/${flowId}/stages/${stageId}/narrate`, {},
      { headers: authHeader(), params: { lang } })
      .then(({ data }) => {
        if (cancelled) return;
        setSlide(data.slide || null);
        setSource(data.source || '');
        setSrc(abs(data.audio_url));
      })
      .catch((e) => { if (!cancelled) toast.error(formatErr(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flowId, stageId, lang, isVideo]);

  // Warm the next stage while this one plays, so the hand-over is seamless.
  useEffect(() => {
    if (!src || loading) return;
    const nextStage = stages[i + 1];
    if (!nextStage || nextStage.video_url) return;
    axios.post(`${API}/sop/flows/${flowId}/stages/${nextStage.id}/narrate`, {},
      { headers: authHeader(), params: { lang } }).catch(() => {});
  }, [src, loading, i, stages, flowId, lang, authHeader]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) return;
    el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [src]);

  const next = () => {
    if (i + 1 < stages.length) { setI(i + 1); return; }
    setDone(true);
    setPlaying(false);
    onFinished?.();
  };

  // Chrome can fire `ended` early when a media transfer is cut short by the
  // proxy. Only advance when playback genuinely reached the end, otherwise
  // resume so a stage is never half-narrated.
  const onEnded = () => {
    const el = audioRef.current;
    if (el && Number.isFinite(el.duration) && el.duration - el.currentTime > 1.5) {
      el.play().catch(() => setPlaying(false));
      return;
    }
    next();
  };

  const onTime = () => {
    const el = audioRef.current;
    if (!el) return;
    setElapsed(el.currentTime || 0);
    if (Number.isFinite(el.duration) && el.duration > 0) {
      setDuration(el.duration);
      setProgress(Math.min(1, el.currentTime / el.duration));
    }
  };

  // Rewind / forward within the stage, and drag anywhere on the timeline.
  const seekBy = (secs) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    el.currentTime = Math.max(0, Math.min(el.duration - 0.2, el.currentTime + secs));
    onTime();
  };

  const seekTo = (fraction) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    el.currentTime = Math.max(0, Math.min(el.duration - 0.2, el.duration * fraction));
    onTime();
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === ' ') { e.preventDefault(); toggle(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); seekBy(-STEP); }
      if (e.key === 'ArrowRight') { e.preventDefault(); seekBy(STEP); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const langLabel = (langs.find((l) => l.code === lang) || {}).label || 'English';

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-50 text-slate-900 overflow-y-auto"
      data-script={lang === 'en' ? undefined : lang}
      data-testid="sop-player"
    >
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="flex items-center gap-3 px-4 sm:px-8 py-3">
          <span className="text-xs uppercase tracking-widest text-teal-700 font-semibold">
            {flow.short_title || 'SOP'}
          </span>
          <span className="text-xs text-slate-500" data-testid="player-progress">
            Stage {Math.min(i + 1, stages.length)} of {stages.length}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1 text-slate-400">
              <Languages size={14} />
            </span>
            <select
              value={lang} onChange={(e) => setLang(e.target.value)}
              className="rounded-full border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-teal-500 focus:outline-none"
              data-testid="player-language"
            >
              {langs.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code === 'en' ? l.label : `${l.native} (${l.label})`}
                </option>
              ))}
            </select>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="player-close"
            >
              <X size={14} /> Close
            </button>
          </div>
        </div>

        {/* journey map — where you are in the customer's journey */}
        <div className="flex items-center gap-1 px-4 sm:px-8 pb-3 overflow-x-auto" data-testid="journey-map">
          {stages.map((s, idx) => {
            const state = idx < i ? 'past' : idx === i ? 'now' : 'ahead';
            return (
              <React.Fragment key={s.id}>
                {idx > 0 && (
                  <span className={`h-0.5 flex-1 min-w-[10px] rounded-full ${
                    state === 'ahead' ? 'bg-slate-200' : 'bg-teal-500'}`}
                  />
                )}
                <button
                  onClick={() => { setDone(false); setI(idx); }}
                  title={s.title}
                  className={`shrink-0 h-6 w-6 rounded-full text-[11px] font-semibold transition-colors ${
                    state === 'now'
                      ? 'bg-teal-600 text-white ring-4 ring-teal-100'
                      : state === 'past'
                        ? 'bg-teal-500 text-white'
                        : 'bg-white text-slate-400 border border-slate-300'}`}
                  data-testid={`journey-stop-${idx}`}
                >
                  {state === 'past' ? <Check size={12} className="mx-auto" /> : idx + 1}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {done ? (
        <div className="px-4 sm:px-8 py-24 max-w-3xl mx-auto text-center sop-rise" data-testid="player-done">
          <CheckCircle2 size={44} className="mx-auto text-teal-600" />
          <h2 className="mt-4 text-2xl font-semibold">Flow complete</h2>
          <p className="mt-2 text-slate-600 text-sm">
            All {stages.length} stages of {flow.short_title || flow.title} watched. Your
            completion has been recorded.
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => { setDone(false); setI(0); }}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
              data-testid="player-restart"
            >
              Watch again
            </button>
            <button
              onClick={onClose}
              className="rounded-full bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
              data-testid="player-finish-close"
            >
              Back to the SOP
            </button>
          </div>
        </div>
      ) : (
        <div key={`${stageId}-${lang}`} className="px-4 sm:px-8 py-6 sm:py-9 max-w-6xl mx-auto sop-slide">
          <div className="grid gap-7 lg:grid-cols-[1.05fr_1fr] lg:items-start">
            {/* picture */}
            <div>
              {isVideo ? (
                <video
                  ref={audioRef} src={src} controls playsInline
                  onEnded={onEnded} onTimeUpdate={onTime}
                  onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                  className="w-full rounded-2xl border border-slate-200 bg-black"
                  data-testid="player-video"
                >
                  <track kind="captions" />
                </video>
              ) : Animation ? (
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 aspect-[3/2]" data-testid="player-animation">
                  <Animation progress={progress} playing={playing} />
                </div>
              ) : view.illustration_url ? (
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 aspect-[3/2]" data-testid="player-illustration">
                  <img
                    src={view.illustration_url} alt={view.title || 'Stage illustration'}
                    className="absolute inset-0 h-full w-full object-cover sop-drift"
                  />
                </div>
              ) : null}
              {!isVideo && (
                <audio
                  ref={audioRef} src={src || undefined} onEnded={onEnded} onTimeUpdate={onTime}
                  onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                  data-testid="player-audio"
                >
                  <track kind="captions" />
                </audio>
              )}

              {/* checklist ticks itself off as the narration plays */}
              {checklist.length > 0 && (
                <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="player-checklist">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">
                    Checklist
                    <span className="ml-2 text-teal-700 font-semibold" data-testid="checklist-count">
                      {Math.min(ticked, checklist.length)}/{checklist.length}
                    </span>
                  </p>
                  <ul className="mt-2.5 space-y-2">
                    {checklist.map((c, idx) => {
                      const on = idx < ticked;
                      return (
                        <li
                          key={`${idx}-${c}`}
                          className={`flex items-start gap-2.5 text-sm sop-tick ${
                            on ? 'text-slate-900' : 'text-slate-400'}`}
                          data-testid={`checklist-item-${idx}`}
                        >
                          <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border sop-tick ${
                            on ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}
                          >
                            {on && <Check size={11} strokeWidth={3} />}
                          </span>
                          <span>{c}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* words — arriving a beat apart rather than all at once */}
            <div>
              {view.role && (
                <span
                  className="sop-rise inline-block rounded-full bg-teal-50 text-teal-700 text-[11px] uppercase tracking-wider px-2.5 py-1 border border-teal-100"
                  style={{ animationDelay: '40ms' }}
                >
                  {view.role}
                </span>
              )}
              <h1
                className="sop-rise mt-3 text-3xl sm:text-4xl font-semibold leading-tight"
                style={{ animationDelay: '140ms' }}
                data-testid="player-stage-title"
              >
                {view.title}
              </h1>
              {view.purpose && (
                <p
                  className="sop-rise mt-3 text-base text-slate-600"
                  style={{ animationDelay: '280ms' }}
                >
                  {view.purpose}
                </p>
              )}
              {slide && (
                <p
                  className="sop-rise mt-2 text-xs text-slate-400"
                  style={{ animationDelay: '340ms' }}
                  data-testid="slide-translated-note"
                >
                  Shown in {langLabel} — translated by Drive IQ from the English SOP
                </p>
              )}

              <h3
                className="sop-rise mt-6 text-[11px] uppercase tracking-wider text-slate-500"
                style={{ animationDelay: '400ms' }}
              >
                What happens
              </h3>
              <ul className="mt-2 space-y-3">
                {(view.activities || []).map((a, idx) => (
                  <li
                    key={`${idx}-${a.activity}`}
                    className="sop-rise text-sm"
                    style={{ animationDelay: `${460 + idx * 110}ms` }}
                  >
                    <span className="text-slate-900 font-medium">{a.activity}</span>
                    {a.why && <span className="block text-slate-500 mt-0.5">{a.why}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {!done && (
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200">
        <div className="flex items-center gap-3 px-4 sm:px-8 pt-3">
          <span className="text-[11px] tabular-nums text-slate-500 w-9" data-testid="player-elapsed">
            {clock(elapsed)}
          </span>
          <input
            type="range" min={0} max={1000} step={1}
            value={Math.round(progress * 1000)}
            onChange={(e) => seekTo(Number(e.target.value) / 1000)}
            disabled={!src}
            aria-label="Seek within this stage"
            className="flex-1 h-1.5 cursor-pointer accent-teal-600 disabled:opacity-40"
            data-testid="player-seek"
          />
          <span className="text-[11px] tabular-nums text-slate-500 w-9 text-right" data-testid="player-duration">
            {clock(duration)}
          </span>
        </div>
        <div className="flex items-center justify-center gap-3 px-4 py-3">
          <button
            onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0}
            className="rounded-full border border-slate-300 p-2.5 text-slate-700 hover:bg-slate-100 disabled:opacity-30 transition-colors"
            title="Previous stage" data-testid="player-prev"
          >
            <SkipBack size={17} />
          </button>
          <button
            onClick={() => seekBy(-STEP)} disabled={!src}
            className="relative rounded-full border border-slate-300 p-2.5 text-slate-700 hover:bg-slate-100 disabled:opacity-30 transition-colors"
            title="Rewind 10 seconds" data-testid="player-rewind"
          >
            <RotateCcw size={17} />
            <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold pt-[1px]">10</span>
          </button>

          {/* progress ring empties as the stage plays */}
          <div className="relative h-[52px] w-[52px]">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 52 52" aria-hidden="true">
              <circle cx="26" cy="26" r="22" fill="none" stroke="#e2e8f0" strokeWidth="3" />
              <circle
                cx="26" cy="26" r="22" fill="none" stroke="#0d9488" strokeWidth="3"
                strokeLinecap="round" strokeDasharray={RING}
                strokeDashoffset={RING * progress}
                style={{ transition: 'stroke-dashoffset 400ms linear' }}
              />
            </svg>
            <button
              onClick={toggle} disabled={loading || !src}
              className="absolute inset-[7px] rounded-full bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-300 transition-colors flex items-center justify-center"
              data-testid="player-toggle"
            >
              {loading ? <Loader2 size={18} className="animate-spin" />
                : playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
          </div>

          <button
            onClick={() => seekBy(STEP)} disabled={!src}
            className="relative rounded-full border border-slate-300 p-2.5 text-slate-700 hover:bg-slate-100 disabled:opacity-30 transition-colors"
            title="Forward 10 seconds" data-testid="player-forward"
          >
            <RotateCw size={17} />
            <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold pt-[1px]">10</span>
          </button>
          <button
            onClick={next}
            className="rounded-full border border-slate-300 p-2.5 text-slate-700 hover:bg-slate-100 transition-colors"
            title="Next stage" data-testid="player-next"
          >
            <SkipForward size={17} />
          </button>
          <span className="ml-2 hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
            <Volume2 size={13} />
            {isVideo ? 'Filmed clip'
              : Animation && !loading ? `Animated · ${langLabel}`
              : loading ? 'Preparing narration…'
                : `Narrated · ${langLabel}`
                  + (source === 'elevenlabs' ? ' · native voice'
                    : source === 'uploaded' ? ' · recorded' : '')}
          </span>
        </div>
        </div>
      )}
    </div>
  );
};

export default SopPlayer;
